import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, Typography, IconButton, Divider } from '@mui/material';
import { X } from 'lucide-react';
import AppButton from './AppButton';
import { TextArea } from './FormInputs';
import { useDispatch } from 'react-redux';
import { updateOpportunityThunk } from '../redux/opportunitySlice';
import { toast } from './Toast/ToastUtils';
const NotesModal = ({ open, onClose, id, initialNotes, onSuccess, onSave, actionLoading = false, className, }) => {
    const [notes, setNotes] = useState(initialNotes);
    const [isSaving, setIsSaving] = useState(false);
    const dispatch = useDispatch();
    useEffect(() => {
        if (open) {
            setNotes(initialNotes);
        }
    }, [open, initialNotes]);
    const handleSave = async () => {
        if (onSave) {
            try {
                setIsSaving(true);
                await onSave(notes);
                if (onSuccess)
                    onSuccess();
                onClose();
            }
            catch (error) {
                // error is already handled by caller or toast
            }
            finally {
                setIsSaving(false);
            }
            return;
        }
        // Fallback for CreateDirectory / ContentOpportunities using updateOpportunityThunk
        if (!id) {
            toast.error('Unable to save notes. Please close and try again.');
            return;
        }
        try {
            setIsSaving(true);
            const data = new FormData();
            data.append('notes', notes);
            const response = await dispatch(updateOpportunityThunk({ id, data })).unwrap();
            if (response.success) {
                toast.success('Notes updated successfully.');
                if (onSuccess)
                    onSuccess();
                onClose();
            }
        }
        catch (error) {
            toast.error(error || 'Failed to update notes');
        }
        finally {
            setIsSaving(false);
        }
    };
    const loading = isSaving || actionLoading;
    return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: "sm", fullWidth: true, PaperProps: {
            className,
            sx: { borderRadius: '8px', overflow: 'hidden' }
        }, sx: {
            zIndex: 2000,
            "& .MuiBackdrop-root": {
                backgroundColor: "rgba(0,0,0,0.5)",
                backdropFilter: className ? "none" : "blur(3px)",
            },
        }, children: [_jsxs(DialogTitle, { sx: { p: 0, display: 'flex', alignItems: 'stretch', justifyContent: 'space-between', bgcolor: '#fff' }, children: [_jsx(Box, { sx: { display: 'flex', alignItems: 'center', px: 2, py: 2 }, children: _jsx(Typography, { sx: { fontSize: '18px', fontWeight: 600, color: '#101828' }, children: "Notes" }) }), _jsx(Box, { className: "notes-close-wrap", sx: { display: 'flex', alignItems: 'center', px: 1.5 }, children: _jsx(IconButton, { onClick: onClose, size: "small", sx: { color: '#667085' }, children: _jsx(X, { size: 20 }) }) })] }), _jsx(Divider, {}), _jsxs(DialogContent, { sx: { p: 2 }, children: [_jsx(Box, { sx: {
                            border: '1px solid #EAECF0',
                            borderRadius: '8px',
                            bgcolor: '#fff',
                            overflow: 'hidden',
                            mb: 2
                        }, children: _jsx(TextArea, { label: "", value: notes, onChange: (e) => setNotes(e.target.value), placeholder: "Enter notes here...", minRows: 10, fullWidth: true, style: { border: 'none', padding: '16px', fontSize: '14px', lineHeight: '1.6' } }) }), _jsx(Box, { children: _jsx(AppButton, { variant: "contained", fullWidth: true, onClick: handleSave, disabled: loading, sx: {
                                bgcolor: '#EE2A7B',
                                '&:hover': { bgcolor: '#D91F6A' },
                                textTransform: 'none',
                                fontWeight: 600,
                                py: 1.5,
                                borderRadius: '8px',
                                fontSize: '16px'
                            }, children: loading ? 'Saving...' : 'Save' }) })] })] }));
};
export default NotesModal;
